import React, { useRef, useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import LocomotiveScroll from 'locomotive-scroll';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Lazy load components for better performance
const Home = lazy(() => import('./Components/Home'));
const About = lazy(() => import('./Components/About'));
const Contact = lazy(() => import('./Components/Contact'));
const Services = lazy(() => import('./Components/Services'));
const Pricing = lazy(() => import('./Components/Pricing'));
const Navbar = lazy(() => import('./Components/Navbar'));
const Footer = lazy(() => import('./Components/Footer'));
const Portfolio = lazy(() => import('./Components/Portfolio'));

// Loading component that shows while lazy components are loading
const LoadingSpinner = () => {

  return (
    <div className="loading-spinner-overlay">
      <div className="loading-spinner-content">
        <div className="loading-dots">
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
        </div>
      </div>
    </div>
  );
};

gsap.registerPlugin(ScrollTrigger);

function App() {
  const location = useLocation();
  const containerRef = useRef(null);
  const locoScrollRef = useRef(null);
  const [deviceType, setDeviceType] = useState('desktop');
  const [performanceMode, setPerformanceMode] = useState('normal');

  // Locomotive Scroll caches layout; after client-side route changes we must reset scroll position,
  // recalculate height, and refresh GSAP — otherwise scrolling stays broken until a full reload.
  useEffect(() => {
    const refreshScroll = () => {
      const loco = locoScrollRef.current;
      if (!loco) return;
      loco.scrollTo(0, { duration: 0, disableLerp: true });
      loco.update();
      ScrollTrigger.refresh();
    };
    refreshScroll();
    const t1 = setTimeout(refreshScroll, 120);
    const t2 = setTimeout(refreshScroll, 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Device detection function
    const detectDevice = () => {
      const width = window.innerWidth;
      if (width <= 768) return 'mobile';
      if (width <= 1024) return 'tablet';
      return 'desktop';
    };

    // Set initial device type
    const currentDevice = detectDevice();
    setDeviceType(currentDevice);

    // Performance mode detection based on device and memory
    const detectPerformanceMode = () => {
      const memory = navigator.deviceMemory;
      const hardwareConcurrency = navigator.hardwareConcurrency;
      
      if (currentDevice === 'mobile' || (memory && memory < 4) || (hardwareConcurrency && hardwareConcurrency < 4)) {
        return 'low';
      } else if (currentDevice === 'tablet' || (memory && memory < 8) || (hardwareConcurrency && hardwareConcurrency < 8)) {
        return 'medium';
      }
      return 'high';
    };
    
    const currentPerformanceMode = detectPerformanceMode();
    setPerformanceMode(currentPerformanceMode);

    // Check if device is mobile or tablet
    const isMobile = currentDevice === 'mobile';
    const isTablet = currentDevice === 'tablet';
    
    // Initialize Locomotive Scroll with optimized settings based on performance mode
    const getScrollSettings = () => {
      if (currentPerformanceMode === 'low') {
        return {
          lerp: isMobile ? 0.4 : isTablet ? 0.3 : 0.2,
          multiplier: isMobile ? 0.5 : isTablet ? 0.7 : 0.9,
          inertia: isMobile ? 0.95 : isTablet ? 0.9 : 0.8,
          touchMultiplier: isMobile ? 0.8 : isTablet ? 1.0 : 1.5,
          safariMultiplier: isMobile ? 0.6 : isTablet ? 0.8 : 1.0
        };
      } else if (currentPerformanceMode === 'medium') {
        return {
          lerp: isMobile ? 0.3 : isTablet ? 0.2 : 0.15,
          multiplier: isMobile ? 0.6 : isTablet ? 0.8 : 1,
          inertia: isMobile ? 0.9 : isTablet ? 0.8 : 0.75,
          touchMultiplier: isMobile ? 1.0 : isTablet ? 1.3 : 1.8,
          safariMultiplier: isMobile ? 0.8 : isTablet ? 1.0 : 1.2
        };
      } else {
        return {
          lerp: isMobile ? 0.25 : isTablet ? 0.15 : 0.1,
          multiplier: isMobile ? 0.7 : isTablet ? 0.9 : 1.1,
          inertia: isMobile ? 0.85 : isTablet ? 0.75 : 0.7,
          touchMultiplier: isMobile ? 1.2 : isTablet ? 1.5 : 2.2,
          safariMultiplier: isMobile ? 0.9 : isTablet ? 1.1 : 1.3
        };
      }
    };
    
    const scrollSettings = getScrollSettings();
    
    const locoScroll = new LocomotiveScroll({
      el: containerRef.current,
      smooth: true,
      ...scrollSettings,
      firefoxMultiplier: 50,
      chromeMultiplier: 1,
      edgeMultiplier: 1,
      scrollFromAnywhere: false,
      smoothMobile: currentPerformanceMode !== 'low', // Disable smooth mobile for low performance
      getDirection: true,
      getSpeed: currentPerformanceMode !== 'low', // Disable speed tracking for low performance
      resetNativeScroll: true,
      reloadOnContextChange: true,
      smartphone: {
        smooth: currentPerformanceMode !== 'low',
        breakpoint: 768,
        ...scrollSettings
      },
      tablet: {
        smooth: true,
        breakpoint: 1024,
        ...scrollSettings
      }
    });

    locoScrollRef.current = locoScroll;

    // Setup GSAP ScrollTrigger
    ScrollTrigger.scrollerProxy(containerRef.current, {
      scrollTop(value) {
        return arguments.length 
          ? locoScroll.scrollTo(value, { duration: 0, disableLerp: true })
          : locoScroll.scroll.instance.scroll.y;
      },
      getBoundingClientRect() {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight
        };
      },
      pinType: containerRef.current.style.transform ? "transform" : "fixed"
    });

    // Update on resize with debouncing for better mobile performance
    let resizeTimeout;
    const onResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Check if device type changed
        const newDevice = detectDevice();
        if (newDevice !== currentDevice) {
          setDeviceType(newDevice);
          // Force reinitialize scroll on device type change
          if (locoScrollRef.current) {
            locoScrollRef.current.destroy();
            // Reinitialize with new settings would happen on next render
          }
        } else {
          // Just update scroll on regular resize
          if (locoScrollRef.current) {
            locoScrollRef.current.update();
            ScrollTrigger.refresh();
          }
        }
      }, 100); // Debounce for 100ms
    };
    window.addEventListener('resize', onResize);

    // Handle visibility change for better mobile performance
    const handleVisibilityChange = () => {
      if (!document.hidden && locoScrollRef.current) {
        // Reinitialize scroll when tab becomes visible again
        setTimeout(() => {
          locoScrollRef.current.update();
          ScrollTrigger.refresh();
        }, 50);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (locoScrollRef.current) {
        locoScrollRef.current.destroy();
        locoScrollRef.current = null;
      }
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      ScrollTrigger.clearMatchMedia();
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  useEffect(() => {
    if (locoScrollRef.current) {
      // Add small timeout to ensure DOM is ready
      setTimeout(() => {
        locoScrollRef.current.update();
        ScrollTrigger.refresh();
      }, 100);
    }
  }, []);

  // Add resize handler for better cross-device support
  useEffect(() => {
    const handleResize = () => {
      if (locoScrollRef.current) {
        locoScrollRef.current.update();
        ScrollTrigger.refresh();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useGSAP(() => {
    // Add GSAP animations here if needed
  });

  return (
    <div
      className="App"
      ref={containerRef}
      data-scroll-container
      data-scroll-direction="vertical"
      data-scroll-speed="1"
      data-device-type={deviceType}
      data-performance-mode={performanceMode}
      style={{
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        contain: 'layout style paint'
      }}
    >
      <Navbar />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/services" element={<Services />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/portfolio" element={<Portfolio />} />
        </Routes>
      </Suspense>
      <Footer />
    </div>
  );
}

function AppWrapper() {
  return (
    <HelmetProvider>
      <Router basename="/">
        <App />
      </Router>
    </HelmetProvider>
  );
}

export default AppWrapper;